'use client';

import { FormEvent, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { apiPost } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

type ContactFormState = {
  name: string;
  email: string;
  company: string;
  details: string;
};

const DEFAULT_DETAILS = "I'd like to schedule a demo of agriQ for our storage operations.";

const EMPTY_CONTACT_FORM: ContactFormState = {
  name: '',
  email: '',
  company: '',
  details: DEFAULT_DETAILS,
};

interface PublicContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PublicContactModal({ open, onOpenChange }: PublicContactModalProps) {
  const [contactForm, setContactForm] =
    useState<ContactFormState>(EMPTY_CONTACT_FORM);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    const { error } = await apiPost('/demo-requests', contactForm);

    setIsSubmitting(false);
    if (error) {
      setSubmitError(error);
      return;
    }

    setIsSubmitted(true);
    setContactForm(EMPTY_CONTACT_FORM);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setIsSubmitted(false);
      setSubmitError(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] rounded-[28px] border-white/70 bg-white/94 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.18)] backdrop-blur-md sm:max-w-lg sm:p-8">
        {isSubmitted ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-[#3f8744]" />
            <h2 className="text-2xl font-bold text-slate-950">Thank you!</h2>
            <p className="text-slate-700">
              Your request has been submitted successfully. The agriQ team will contact you shortly.
            </p>
          </div>
        ) : (
        <>
        <DialogHeader className="mb-2">
          <DialogTitle className="text-2xl font-bold text-slate-950">
            Request a Demo
          </DialogTitle>
          <DialogDescription className="text-slate-700">
            Leave your details and we&apos;ll get back to you soon.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleContactSubmit}>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-800" htmlFor="contact-name">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="contact-name"
              value={contactForm.name}
              onChange={(event) =>
                setContactForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-800" htmlFor="contact-email">
              Email <span className="text-red-500">*</span>
            </label>
            <Input
              id="contact-email"
              type="email"
              value={contactForm.email}
              onChange={(event) =>
                setContactForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-800" htmlFor="contact-company">
              Company <span className="text-red-500">*</span>
            </label>
            <Input
              id="contact-company"
              value={contactForm.company}
              onChange={(event) =>
                setContactForm((current) => ({
                  ...current,
                  company: event.target.value,
                }))
              }
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-800" htmlFor="contact-details">
              Message / Details
            </label>
            <Textarea
              id="contact-details"
              rows={4}
              value={contactForm.details}
              onChange={(event) =>
                setContactForm((current) => ({
                  ...current,
                  details: event.target.value,
                }))
              }
            />
          </div>

          {submitError && (
            <p className="text-sm text-red-600">{submitError}</p>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="h-12 rounded-2xl bg-[#3f8744] px-7 text-base font-semibold text-white hover:bg-[#2f6a35]"
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
